/**
 * ===================================================================
 * PREMIUM SEARCH ENHANCEMENT - COMPLETE VERSION
 * ===================================================================
 * Features:
 * - Typewriter placeholder animation
 * - Trending searches dropdown on focus
 * - Popular products display
 * - Live predictive search integration
 * - Click outside to close
 * - Reset button closes everything
 * - Mobile responsive
 * - Keyboard accessible (ESC to close)
 * ===================================================================
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  const CONFIG = {
    typewriterPhrases: [
      'Search protein powders...',
      'Search supplements...',
      'Search best sellers...',
      'Search pre-workout...',
      'Search vitamins...',
      'Search gym gear...'
    ],
    typewriterSpeed: 80,      // Typing speed (ms)
    typewriterDelay: 2000,    // Delay before next phrase (ms)
    deleteSpeed: 50,          // Deleting speed (ms)
    searchDelay: 300          // Debounce delay for search (ms)
  };

  // ===== TYPEWRITER ANIMATION CLASS =====
  class TypewriterPlaceholder {
    constructor(input, phrases) {
      this.input = input;
      this.phrases = phrases;
      this.phraseIndex = 0;
      this.charIndex = 0;
      this.isDeleting = false;
      this.isActive = true;
      this.originalPlaceholder = input.placeholder;
      
      this.init();
    }

    init() {
      // Start animation after short delay
      setTimeout(() => this.type(), 500);
      
      // Pause animation when input is focused or has value
      this.input.addEventListener('focus', () => {
        this.isActive = false;
        this.input.placeholder = this.originalPlaceholder;
      });
      
      this.input.addEventListener('blur', () => {
        if (this.input.value === '') {
          this.isActive = true;
          this.type();
        }
      });
      
      this.input.addEventListener('input', () => {
        if (this.input.value.length > 0) {
          this.isActive = false;
        }
      });
    }

    type() {
      if (!this.isActive) return;

      const currentPhrase = this.phrases[this.phraseIndex];
      
      if (this.isDeleting) {
        // Deleting characters
        this.charIndex--;
        this.input.placeholder = currentPhrase.substring(0, this.charIndex);
        
        if (this.charIndex === 0) {
          this.isDeleting = false;
          this.phraseIndex = (this.phraseIndex + 1) % this.phrases.length;
          setTimeout(() => this.type(), 500);
          return;
        }
        
        setTimeout(() => this.type(), CONFIG.deleteSpeed);
      } else {
        // Typing characters
        this.charIndex++;
        this.input.placeholder = currentPhrase.substring(0, this.charIndex);
        
        if (this.charIndex === currentPhrase.length) {
          this.isDeleting = true;
          setTimeout(() => this.type(), CONFIG.typewriterDelay);
          return;
        }
        
        setTimeout(() => this.type(), CONFIG.typewriterSpeed);
      }
    }
  }

  // ===== PREMIUM DROPDOWN MANAGER =====
  class PremiumSearchDropdown {
    constructor(input, dropdown) {
      this.input = input;
      this.dropdown = dropdown;
      this.form = input.closest('form');
      this.resetButton = this.form ? this.form.querySelector('button[type="reset"]') : null;
      this.searchContainer = input.closest('.header-search');
      this.trendingSection = dropdown.querySelector('[data-trending-section]');
      this.popularSection = dropdown.querySelector('[data-popular-section]');
      this.predictiveSearch = document.querySelector('[data-predictive-search]');
      this.isOpen = false;
      
      this.init();
    }

    init() {
      // Show dropdown on focus (if input is empty)
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() === '') {
          this.showDropdown();
          this.hidePredictiveSearch();
        }
      });

      // Hide dropdown on blur (with delay to allow clicks)
      this.input.addEventListener('blur', (e) => {
        setTimeout(() => {
          if (!this.dropdown.contains(document.activeElement)) {
            this.hideDropdown();
          }
        }, 200);
      });

      // Handle input changes
      this.input.addEventListener('input', () => {
        if (this.input.value.trim() === '') {
          this.showDropdown();
          this.hidePredictiveSearch();
        } else {
          this.hideDropdown();
          this.showPredictiveSearch();
        }
      });

      // ✨ NEW: Handle reset button click - Close everything
      if (this.resetButton) {
        this.resetButton.addEventListener('click', (e) => {
          setTimeout(() => {
            this.hideDropdown();
            this.hidePredictiveSearch();
            this.input.value = '';
            this.input.blur();
            
            // Hide the reset button itself
            if (this.resetButton.classList.contains('d-none') === false) {
              this.resetButton.classList.add('d-none');
            }
          }, 50);
        });
      }

      // Handle trending search clicks
      const trendingLinks = this.dropdown.querySelectorAll('[data-trending-term]');
      trendingLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const term = link.getAttribute('data-trending-term');
          this.input.value = term;
          this.input.form.submit();
        });
      });

      // ✨ NEW: Close dropdown AND predictive search when clicking outside
      document.addEventListener('click', (e) => {
        const isSearchContainerClick = this.searchContainer && this.searchContainer.contains(e.target);
        const isPredictiveSearchClick = this.predictiveSearch && this.predictiveSearch.contains(e.target);
        const isDropdownClick = this.dropdown.contains(e.target);
        
        // If click is outside all search-related elements, close everything
        if (!isSearchContainerClick && !isPredictiveSearchClick && !isDropdownClick) {
          this.hideDropdown();
          this.hidePredictiveSearch();
        }
      });

      // ✨ NEW: Enhanced keyboard navigation
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.hideDropdown();
          this.hidePredictiveSearch();
          this.input.blur();
        }
      });

      // ✨ NEW: Close predictive search when clicking anywhere outside
      if (this.predictiveSearch) {
        document.addEventListener('click', (e) => {
          if (!this.searchContainer.contains(e.target) && 
              !this.predictiveSearch.contains(e.target)) {
            this.hidePredictiveSearch();
          }
        });
      }
    }

    showDropdown() {
      this.dropdown.style.display = 'block';
      setTimeout(() => {
        this.dropdown.classList.add('is-active');
      }, 10);
      this.isOpen = true;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hideDropdown() {
      this.dropdown.classList.remove('is-active');
      setTimeout(() => {
        this.dropdown.style.display = 'none';
      }, 300);
      this.isOpen = false;
      this.input.setAttribute('aria-expanded', 'false');
    }

    showPredictiveSearch() {
      if (this.predictiveSearch) {
        this.predictiveSearch.style.display = 'block';
        setTimeout(() => {
          this.predictiveSearch.classList.add('is-active');
        }, 10);
      }
    }

    hidePredictiveSearch() {
      if (this.predictiveSearch) {
        this.predictiveSearch.classList.remove('is-active');
        setTimeout(() => {
          this.predictiveSearch.style.display = 'none';
        }, 300);
      }
    }
  }

  // ===== ENHANCED PREDICTIVE SEARCH RESULTS =====
  class EnhancedPredictiveSearch {
    constructor() {
      this.init();
    }

    init() {
      // Listen for Shopify's predictive search results
      const observerCallback = (mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            this.enhanceResults();
          }
        });
      };

      const observer = new MutationObserver(observerCallback);

      // Start observing when DOM is ready
      const startObserving = () => {
        const resultsContainer = document.getElementById('predictive-search-results');
        if (resultsContainer) {
          observer.observe(resultsContainer, {
            childList: true,
            subtree: true
          });
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startObserving);
      } else {
        startObserving();
      }
    }

    enhanceResults() {
      const productItems = document.querySelectorAll('.predictive-search__item');
      
      // Add premium styling and hover effects
      productItems.forEach(item => {
        if (!item.classList.contains('enhanced')) {
          item.classList.add('enhanced');
          
          // Add smooth hover animation
          item.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-2px)';
          });
          
          item.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0)';
          });
        }
      });
    }
  }

  // ===== SEARCH FORM ENHANCEMENT =====
  class SearchFormEnhancer {
    constructor() {
      this.init();
    }

    init() {
      const searchForms = document.querySelectorAll('.header-search__form');
      
      searchForms.forEach(form => {
        const input = form.querySelector('input[type="search"]');
        const resetButton = form.querySelector('button[type="reset"]');
        
        if (input && resetButton) {
          // Toggle reset button visibility
          input.addEventListener('input', () => {
            if (input.value.length > 0) {
              resetButton.classList.remove('d-none');
            } else {
              resetButton.classList.add('d-none');
            }
          });
        }
      });
    }
  }

  // ===== INITIALIZATION =====
  function initPremiumSearch() {
    const searchInput = document.querySelector('[data-search-input]');
    const premiumDropdown = document.querySelector('[data-premium-dropdown]');

    if (!searchInput) {
      console.warn('Premium Search: Search input not found');
      return;
    }

    // Initialize Typewriter Animation
    if (searchInput.getAttribute('data-typewriter-enabled') === 'true') {
      new TypewriterPlaceholder(searchInput, CONFIG.typewriterPhrases);
    }

    // Initialize Premium Dropdown
    if (premiumDropdown) {
      new PremiumSearchDropdown(searchInput, premiumDropdown);
    } else {
      console.warn('Premium Search: Dropdown container not found');
    }

    // Initialize Enhanced Predictive Search
    new EnhancedPredictiveSearch();

    // Initialize Search Form Enhancements
    new SearchFormEnhancer();

    console.log('✅ Premium Search initialized successfully');
  }

  // ===== RUN ON DOM READY =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPremiumSearch);
  } else {
    initPremiumSearch();
  }

  // ===== EXTEND EXISTING SEARCHFORM CLASS (if exists) =====
  if (typeof SearchForm !== 'undefined' && SearchForm.prototype) {
    const originalOnChange = SearchForm.prototype.onChange;
    
    SearchForm.prototype.onChange = function() {
      if (originalOnChange) {
        originalOnChange.call(this);
      }
      
      // Additional premium features
      const premiumDropdown = document.querySelector('[data-premium-dropdown]');
      const predictiveSearch = document.querySelector('[data-predictive-search]');
      
      if (this.input.value.trim() === '') {
        if (premiumDropdown) {
          premiumDropdown.style.display = 'block';
          setTimeout(() => premiumDropdown.classList.add('is-active'), 10);
        }
        if (predictiveSearch) {
          predictiveSearch.classList.remove('is-active');
          setTimeout(() => predictiveSearch.style.display = 'none', 300);
        }
      } else {
        if (premiumDropdown) {
          premiumDropdown.classList.remove('is-active');
          setTimeout(() => premiumDropdown.style.display = 'none', 300);
        }
        if (predictiveSearch) {
          predictiveSearch.style.display = 'block';
          setTimeout(() => predictiveSearch.classList.add('is-active'), 10);
        }
      }
    };

    const originalOnFormReset = SearchForm.prototype.onFormReset;
    
    SearchForm.prototype.onFormReset = function(event) {
      if (originalOnFormReset) {
        originalOnFormReset.call(this, event);
      }
      
      // Close all dropdowns on reset
      setTimeout(() => {
        const premiumDropdown = document.querySelector('[data-premium-dropdown]');
        const predictiveSearch = document.querySelector('[data-predictive-search]');
        
        if (premiumDropdown) {
          premiumDropdown.classList.remove('is-active');
          setTimeout(() => premiumDropdown.style.display = 'none', 300);
        }
        if (predictiveSearch) {
          predictiveSearch.classList.remove('is-active');
          setTimeout(() => predictiveSearch.style.display = 'none', 300);
        }
        
        this.input.blur();
      }, 50);
    };
  }

  // ===== EXPOSE PUBLIC API =====
  window.PremiumSearch = {
    version: '1.0.0',
    config: CONFIG,
    init: initPremiumSearch
  };

})();